import type { components } from '@/types/api/v1'
import { useMemo, useState } from 'react'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'

export interface FilterOptions {
  searchText: string
  minVolume: number | null
  maxVolume: number | null
  includeMatched: boolean
  includeOnlyMatchingTerminals: boolean
}

// Interface for invoice data in filtering
interface FilterInvoiceData {
  invoice_number?: string
  terminal_name?: string
  terminal?: {
    internal_name?: string
    source_name?: string
  }
  linked_bol_ids?: string[]
  linked_bol_numbers?: string[]
  original?: boolean
  details?: Array<{
    gross_volume?: number
  }>
}

// Interface for BOL data in filtering
interface FilterBolData {
  bol_number?: string
  terminal_name?: string | null
  invoice_id?: string | null
  invoice_number?: string | null
  invoice_total?: number | null
  linked_invoice_ids?: string[]
  linked_invoice_numbers?: string[]
  products?: Array<{
    gross_volume?: number
  }>
}
type BolItem = components['schemas']['BolCandidate']
type InvoiceItem = components['schemas']['InvoiceCandidate']
// Union type for filter data
type FilterData = BolItem | InvoiceItem

interface UseManageMatchFiltersParams {
  rawData: FilterData[]
  searchType: 'invoice' | 'bol'
  matchDetails: MatchDetailResponse
  currentMatchDocuments: {
    invoices: InvoiceItem[]
    bols: BolItem[]
  }
}

/**
 * Hook that handles all filtering logic for the Manage Match section
 */
export function useManageMatchFilters({ rawData, searchType, currentMatchDocuments }: UseManageMatchFiltersParams) {
  // Filter state
  const [searchText, setSearchText] = useState('')
  const [minVolume, setMinVolume] = useState<number | null>(null)
  const [maxVolume, setMaxVolume] = useState<number | null>(null)
  const [includeMatched, setIncludeMatched] = useState(false) // Default off - filter out matched items
  const [includeOnlyMatchingTerminals, setIncludeOnlyMatchingTerminals] = useState(true) // Default on
  const [includeOnlyMatchingSuppliers, setIncludeOnlyMatchingSuppliers] = useState(true)

  // Get match terminals for filtering - use current match documents, not original match details
  const matchTerminals = useMemo(() => {
    const terminals = new Set<string>()
    // Get terminals from current invoices
    currentMatchDocuments.invoices.forEach((invoice) => {
      let terminalName: string | undefined
      if (invoice.original) {
        // Original match items use terminal_name
        terminalName = invoice.terminal_name
      } else {
        // Potential matches use terminal.internal_name || terminal.source_name
        terminalName = invoice.terminal?.internal_name || invoice.terminal?.source_name
      }

      if (terminalName) {
        terminals.add(terminalName.toLowerCase())
      }
    })

    // Get terminals from current BOLs
    currentMatchDocuments.bols.forEach((bol) => {
      if (bol.terminal_name) {
        terminals.add(bol.terminal_name.toLowerCase())
      }
    })

    return terminals
  }, [currentMatchDocuments])

  const matchSuppliers = useMemo(() => {
    const result = new Set<string>()
    currentMatchDocuments.bols.forEach((x) => {
      if (x.supplier_name) {
        result.add(x.supplier_name.toLowerCase())
      }
    })
    currentMatchDocuments.invoices.forEach((x) => {
      const supplierName = x.supplier?.internal_name ?? x.supplier?.source_name
      if (supplierName) {
        result.add(supplierName.toLowerCase())
      }
    })
    return result
  }, [currentMatchDocuments])

  // Apply all filters to the data
  const filteredData = useMemo(() => {
    if (!rawData) return []

    if (searchType === 'bol') {
      const currentMatchedIds = new Set<string>(currentMatchDocuments.bols.map((x) => x.bol_id))
      return (rawData as BolItem[])
        .map((x) => ({ ...x, volume: x.products.reduce((sum, p) => sum + p.gross_volume, 0) }))
        .filter(
          (x) =>
            (!searchText || x.bol_number.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) &&
            (!minVolume || x.volume >= minVolume) &&
            (!maxVolume || x.volume <= maxVolume) &&
            (includeMatched ||
              currentMatchedIds.has(x.bol_id) ||
              !x.linked_invoice_ids.length ||
              !x.linked_invoice_numbers.length ||
              !x.linked_invoice_total) &&
            (!includeOnlyMatchingTerminals || matchTerminals.has(x.terminal_name?.toLowerCase() ?? '')) &&
            (!includeOnlyMatchingSuppliers || matchSuppliers.has(x.supplier_name?.toLowerCase() ?? ''))
        )
    }
    const currentMatchedIds = new Set<string>(currentMatchDocuments.invoices.map((x) => x.invoice_id))
    return (rawData as InvoiceItem[])
      .map((x) => ({ ...x, volume: x.details.reduce((sum, detail) => sum + (detail.gross_volume ?? 0), 0) }))
      .filter(
        (x) =>
          (!searchText || x.invoice_number.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())) &&
          (!minVolume || x.volume >= minVolume) &&
          (!maxVolume || x.volume <= maxVolume) &&
          (includeMatched ||
            currentMatchedIds.has(x.invoice_id) ||
            !x.linked_bol_ids.length ||
            !x.linked_bol_numbers.length ||
            !x.linked_bol_total) &&
          (!includeOnlyMatchingTerminals ||
            matchTerminals.has((x.terminal?.internal_name ?? x.terminal?.source_name)?.toLowerCase() ?? '')) &&
          (!includeOnlyMatchingSuppliers ||
            matchSuppliers.has((x.supplier?.internal_name ?? x.supplier?.source_name)?.toLowerCase() ?? ''))
      )
  }, [
    rawData,
    searchText,
    searchType,
    minVolume,
    maxVolume,
    includeMatched,
    includeOnlyMatchingTerminals,
    includeOnlyMatchingSuppliers,
    currentMatchDocuments,
    matchTerminals,
    matchSuppliers,
  ])

  // Helper function to check if an item is matched (for UI indicators)
  const isItemMatched = (item: FilterData): boolean => {
    if (searchType === 'invoice') {
      const invoiceItem = item as FilterInvoiceData
      return (
        (invoiceItem.linked_bol_ids && invoiceItem.linked_bol_ids.length > 0) ||
        (invoiceItem.linked_bol_numbers && invoiceItem.linked_bol_numbers.length > 0)
      )
    } else {
      const bolItem = item as FilterBolData
      return (
        !!bolItem.invoice_id ||
        !!bolItem.invoice_number ||
        (!!bolItem.invoice_total && bolItem.invoice_total > 0) ||
        (bolItem.linked_invoice_ids && bolItem.linked_invoice_ids.length > 0) ||
        (bolItem.linked_invoice_numbers && bolItem.linked_invoice_numbers.length > 0)
      )
    }
  }

  // Helper function to get linked numbers for display
  const getLinkedNumbers = (item: FilterData): string[] => {
    if (searchType === 'invoice') {
      const invoiceItem = item as FilterInvoiceData
      return invoiceItem.linked_bol_numbers || []
    } else {
      const bolItem = item as FilterBolData
      // Return linked invoice numbers array if it exists, or single invoice_number if it exists
      const linkedNumbers = bolItem.linked_invoice_numbers || []
      if (bolItem.invoice_number && !linkedNumbers.includes(bolItem.invoice_number)) {
        return [...linkedNumbers, bolItem.invoice_number]
      }
      return linkedNumbers
    }
  }

  // Reset all filters
  const resetFilters = () => {
    setSearchText('')
    setMinVolume(null)
    setMaxVolume(null)
    setIncludeMatched(false)
    setIncludeOnlyMatchingTerminals(true)
    setIncludeOnlyMatchingSuppliers(true)
  }

  return {
    // Filter state
    searchText,
    setSearchText,
    minVolume,
    setMinVolume,
    maxVolume,
    setMaxVolume,
    includeMatched,
    setIncludeMatched,
    includeOnlyMatchingTerminals,
    setIncludeOnlyMatchingTerminals,
    includeOnlyMatchingSuppliers,
    setIncludeOnlyMatchingSuppliers,

    // Computed data
    filteredData,

    // Helper functions
    isItemMatched,
    getLinkedNumbers,
    resetFilters,

    // Stats
    totalItems: rawData?.length || 0,
    filteredItems: filteredData.length,
    matchTerminals: Array.from(matchTerminals),
  }
}
